import { type ChangeEvent, type DragEvent, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { cx } from "../cx";
import { UploadIcon } from "../icons";
import { Button } from "./Button";

/**
 * The dashed drop zone of the Upload screen. A real file input sits inside the
 * label so click, keyboard, and drag all end in `onFiles`; the caller sniffs
 * the file (the server rejects PDF with 415 either way).
 */
export function FileDrop({
  onFiles,
  accept = ".docx,.epub,.md,.markdown",
  file,
  onClear,
  hint,
  error,
  disabled = false,
  className = "",
}: {
  onFiles: (files: File[]) => void;
  accept?: string;
  /** The chosen file, shown with a clear button. */
  file?: { name: string; size?: number } | null;
  onClear?: () => void;
  /** Replaces the default "Drop a DOCX, EPUB, or Markdown file". */
  hint?: ReactNode;
  error?: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation("components");
  const id = useId();
  const [over, setOver] = useState(false);

  const pick = (list: FileList | null) => {
    const files = list ? Array.from(list) : [];
    if (files.length > 0) onFiles(files);
  };
  const onDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setOver(false);
    if (!disabled) pick(e.dataTransfer.files);
  };
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    pick(e.target.files);
    e.target.value = "";
  };

  return (
    <div
      data-component="FileDrop"
      // What a guided attempt focuses: the label's htmlFor is inside, so the
      // focus lands on the real file input under the sr-only class.
      aria-invalid={error ? "true" : undefined}
      className={cx("flex flex-col gap-2", className)}
    >
      <label
        htmlFor={id}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        data-over={over || undefined}
        className={cx(
          "flex h-[150px] flex-col items-center justify-center gap-2 rounded-md border border-dashed text-ui",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          over ? "border-accent bg-accent-soft" : "border-line-strong bg-sunken",
          error ? "border-danger" : null,
        )}
      >
        <input
          id={id}
          type="file"
          accept={accept}
          disabled={disabled}
          onChange={onChange}
          className="sr-only"
        />
        <UploadIcon size={24} className="text-ink-2" />
        <span className="font-medium text-ink">{hint ?? t("fileDrop.hint")}</span>
        <span className="text-ink-3">{t("fileDrop.choose")}</span>
      </label>
      {file ? (
        <div className="flex items-center gap-3 font-mono text-data text-ink-2">
          <span className="min-w-0 truncate">{t("fileDrop.selected", { name: file.name })}</span>
          {onClear ? (
            <Button variant="quiet" size="sm" onClick={onClear} disabled={disabled}>
              {t("fileDrop.clear")}
            </Button>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-data text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
